import { Box, Card, Group, Paper, SimpleGrid, Skeleton, Stack, Table } from '@mantine/core'

/**
 * The shape of the page, while its data is on the way.
 *
 * A centred spinner says "something is happening somewhere"; an outline of
 * the board says "your columns are about to be here", and the layout does not
 * jump when they arrive. It is the same wait either way - this is the half of
 * loading that is about the eye rather than the network.
 *
 * Only ever shown when there is nothing cached to draw instead. After the
 * first visit the persisted cache usually wins the race and none of this is
 * seen.
 */
export function BoardSkeleton() {
  return (
    <Group align="flex-start" gap="md" wrap="nowrap" h="100%" pb="xs">
      {[0, 1, 2, 3].map((column) => (
        <Stack key={column} gap="sm" w={280} style={{ flexShrink: 0 }}>
          <Group justify="space-between">
            <Skeleton height={14} width={90} radius="sm" />
            <Skeleton height={14} width={20} radius="sm" />
          </Group>

          {/* Fewer cards further right, which is what a board usually looks
              like and keeps the placeholder from reading as a grid. */}
          {Array.from({ length: Math.max(1, 4 - column) }).map((_, card) => (
            <Card key={card} padding="sm" radius="md">
              <Stack gap={8}>
                <Skeleton height={10} radius="sm" />
                <Skeleton height={10} width="60%" radius="sm" />
                <Group gap={6}>
                  <Skeleton height={14} width={38} radius="xl" />
                  <Skeleton height={14} width={28} radius="xl" />
                </Group>
              </Stack>
            </Card>
          ))}
        </Stack>
      ))}
    </Group>
  )
}

/**
 * The project list, before it arrives.
 *
 * It draws the sheet's own shape - a header strip, then rows of the same
 * height with blocks in the same columns - because a skeleton that does not
 * match what replaces it is worse than none: the layout jumps at the moment
 * somebody has started reading it. This used to draw a grid of cards, which
 * the list stopped being.
 */
export function ProjectGridSkeleton() {
  return (
    <Paper radius={12} p={0} withBorder style={{ overflow: 'hidden' }}>
      {/* The header strip, in the sheet's own header grey. */}
      <Box
        px={16}
        py={11}
        style={{
          background: 'var(--mantine-color-gray-0)',
          borderBottom: '1px solid var(--mantine-color-gray-3)',
        }}
      >
        <Skeleton height={10} width={180} radius="sm" />
      </Box>

      {Array.from({ length: 6 }).map((_, index) => (
        <Group
          key={index}
          h={60}
          px={16}
          gap={0}
          wrap="nowrap"
          style={{
            borderBottom:
              index === 5 ? undefined : '1px solid var(--mantine-color-gray-2)',
          }}
        >
          {/* Project: the colour edge, the code chip and the name. */}
          <Group gap={10} wrap="nowrap" style={{ flex: 2, minWidth: 0 }}>
            <Skeleton height={26} width={3} radius="sm" />
            <Skeleton height={18} width={44} radius="sm" />
            <Skeleton height={11} width="55%" radius="sm" />
          </Group>

          {/* Progress. */}
          <Group gap={10} wrap="nowrap" style={{ flex: 1.4, minWidth: 0 }}>
            <Skeleton height={5} radius="xl" style={{ flex: 1 }} />
            <Skeleton height={10} width={28} radius="sm" />
          </Group>

          {/* The two counts. */}
          <Group gap={16} wrap="nowrap" style={{ flex: 1 }}>
            <Skeleton height={11} width={30} radius="sm" />
            <Skeleton height={11} width={30} radius="sm" />
          </Group>

          {/* The overlapping faces. */}
          <Group gap={0} wrap="nowrap" style={{ flex: 1 }}>
            <Skeleton height={26} circle />
            <Skeleton height={26} circle ml={-8} />
          </Group>

          <Box style={{ flex: 1 }}>
            <Skeleton height={20} width={72} radius={8} />
          </Box>

          <Box style={{ flex: 1 }}>
            <Skeleton height={11} width={80} radius="sm" />
          </Box>

          <Skeleton height={16} width={16} radius="sm" />
        </Group>
      ))}
    </Paper>
  )
}

export function TaskSkeleton() {
  return (
    <Stack gap="md">
      <Skeleton height={12} width={220} radius="sm" />
      <Card padding={0} radius="md" withBorder>
        <Stack gap="md" p="lg">
          <Skeleton height={20} width="45%" radius="sm" />
          <Skeleton height={10} width={180} radius="sm" />
        </Stack>
        <Group align="flex-start" gap={0} wrap="nowrap">
          <Stack gap="md" p="lg" style={{ flex: 1 }}>
            <Skeleton height={10} radius="sm" />
            <Skeleton height={10} width="80%" radius="sm" />
            <Skeleton height={10} width="60%" radius="sm" />
            <Skeleton height={90} radius="md" mt="md" />
          </Stack>
          <Stack gap="md" p="lg" w={260} style={{ flexShrink: 0 }}>
            {[0, 1, 2, 3].map((field) => (
              <Stack key={field} gap={6}>
                <Skeleton height={9} width={60} radius="sm" />
                <Skeleton height={30} radius="sm" />
              </Stack>
            ))}
          </Stack>
        </Group>
      </Card>
    </Stack>
  )
}

/**
 * Rows of something: a feed, a list of people, a set of comments.
 *
 * Takes the row height so the placeholder occupies what the real thing will,
 * and the page does not jump when it arrives.
 */
export function ListSkeleton({
  rows = 6,
  height = 44,
  withAvatar = false,
}: {
  rows?: number
  height?: number
  withAvatar?: boolean
}) {
  return (
    <Stack gap={8}>
      {Array.from({ length: rows }).map((_, index) => (
        <Group key={index} gap="sm" wrap="nowrap" h={height}>
          {withAvatar && <Skeleton height={28} circle />}
          <Stack gap={6} style={{ flex: 1 }}>
            <Skeleton height={9} width={`${70 - (index % 3) * 12}%`} radius="sm" />
            <Skeleton height={8} width={`${45 - (index % 4) * 6}%`} radius="sm" />
          </Stack>
        </Group>
      ))}
    </Stack>
  )
}

/** A panel inside a task: comments, files, related work. */
export function PanelSkeleton({ rows = 2 }: { rows?: number }) {
  return (
    <Stack gap="sm">
      {Array.from({ length: rows }).map((_, index) => (
        <Group key={index} gap="sm" wrap="nowrap" align="flex-start">
          <Skeleton height={24} circle />
          <Stack gap={6} style={{ flex: 1 }}>
            <Skeleton height={8} width="30%" radius="sm" />
            <Skeleton height={8} radius="sm" />
            <Skeleton height={8} width="65%" radius="sm" />
          </Stack>
        </Group>
      ))}
    </Stack>
  )
}

export function TableSkeleton({ rows = 6, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          {Array.from({ length: columns }).map((_, index) => (
            <Table.Th key={index}>
              <Skeleton height={9} width="60%" radius="sm" />
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {Array.from({ length: rows }).map((_, row) => (
          <Table.Tr key={row}>
            {Array.from({ length: columns }).map((_, column) => (
              <Table.Td key={column}>
                <Skeleton height={9} width={column === 0 ? '80%' : '50%'} radius="sm" />
              </Table.Td>
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

/** The summary's two chart cards. */
export function ChartsSkeleton() {
  return (
    <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
      {[0, 1].map((card) => (
        <Card key={card} withBorder radius="md" padding="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Skeleton height={11} width={150} radius="sm" />
              <Skeleton height={9} width={110} radius="sm" />
            </Group>
            <Group gap="md" justify="center">
              {[0, 1, 2].map((key) => (
                <Skeleton key={key} height={8} width={60} radius="sm" />
              ))}
            </Group>
            <Group align="flex-end" gap="sm" h={210} px="xs">
              {[60, 30, 85, 45, 70, 25, 95, 50].map((height, index) => (
                <Skeleton key={index} height={`${height}%`} style={{ flex: 1 }} radius="sm" />
              ))}
            </Group>
          </Stack>
        </Card>
      ))}
    </SimpleGrid>
  )
}

/** One dashboard widget, while its own data is on the way. */
export function WidgetSkeleton() {
  return (
    <Stack gap={10} p="xs">
      <Skeleton height={9} width="45%" radius="sm" />
      <Skeleton height={26} width="30%" radius="sm" />
      <Skeleton height={8} radius="sm" />
      <Skeleton height={8} width="70%" radius="sm" />
    </Stack>
  )
}

/** The widget grid, before anybody's layout has arrived. */
export function DashboardSkeleton() {
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
      {Array.from({ length: 6 }).map((_, index) => (
        <Card key={index} withBorder radius="md" padding="md" mih={index % 3 === 0 ? 220 : 160}>
          <WidgetSkeleton />
        </Card>
      ))}
    </SimpleGrid>
  )
}
